import { Button, DialogContent, DialogTitle, TextField } from "@mui/material";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useState } from "react";
import type { HandledApiResponse } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import type { MeasureSubgroupWithMeasures } from "../../../../../../../../../api/types/svTypes.ts";

interface Props {
  title: string;
  initialName?: string;
  validateName: (name: string) => string | undefined;
  onSave: (trimmedName: string) => Promise<HandledApiResponse<MeasureSubgroupWithMeasures>>;
  onSaved: (response: MeasureSubgroupWithMeasures) => void;
  onSaveError: (error: ApiError) => void;
  onClose: () => void;
}

const SubgroupNameDialog = ({ title, initialName, validateName, onSave, onSaved, onSaveError, onClose }: Props) => {
  const [name, setName] = useState(initialName ?? "");
  const [serverError, setServerError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const trimmedName = name.trim();
  const validationError = validateName(name);
  const fieldError = serverError ?? (trimmedName ? validationError : undefined);
  const isUnchanged = trimmedName === initialName;
  const canSave = !isUnchanged && validationError === undefined && serverError === undefined && !saving;

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setServerError(undefined);
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    const [response, error] = await onSave(trimmedName);
    setSaving(false);

    if (error) {
      if (error.type === "AlreadyExist") {
        setServerError(error.message);
        return;
      }
      onSaveError(error);
      return;
    }

    onSaved(response);
    onClose();
  };

  const handleNameKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void handleSave();
    }
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Name"
          value={name}
          error={fieldError !== undefined}
          helperText={fieldError}
          onChange={handleNameChange}
          onKeyDown={handleNameKeyDown}
        />
      </DialogContent>
      <DialogButtons>
        <Button variant="text" color="secondary" disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} loading={saving} onClick={handleSave}>
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default SubgroupNameDialog;
